import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

function AddProduct() {
  const { user, authLoaded } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    brand: '',
    category: '',
    description: '',
    price: '0',
    countInStock: '0',
    images: [], // File objects
  });

  const [preview, setPreview] = useState([]); // preview URLs
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoaded) return;
    if (!user) return navigate('/login');
    if (!user.isAdmin) return navigate('/');
  }, [authLoaded, user, navigate]);

  // Add image
  const handleAddImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (form.images.length >= 3) return alert('Maximum 3 images allowed');

    setForm({ ...form, images: [...form.images, file] });
    setPreview([...preview, URL.createObjectURL(file)]);

    // Reset file input to allow selecting same file again
    e.target.value = null;
  };

  // Remove image
  const handleRemoveImage = (index) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== index) });
    setPreview(preview.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) return alert('Upload at least one image');

    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('brand', form.brand);
    formData.append('category', form.category);
    formData.append('description', form.description);
    formData.append('price', parseFloat(form.price)); // ensure number
    formData.append('countInStock', parseInt(form.countInStock)); // ensure number

    form.images.forEach((img) => formData.append('photos', img));

    setLoading(true);
    try {
      await axios.post('https://apimyshop.myidealtd.co.uk/api/products/add-product', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      alert('Product added successfully!');
      navigate('/');
    } catch (err) {
      console.error(err);
      // Show backend errors (Multer or validation)
      alert(err.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="my-5">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <Card className="shadow-sm p-4">
            <h3 className="text-center mb-4">Add New Product</h3>
            <Form onSubmit={handleSubmit}>
              {/* Name & Brand */}
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Product Name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Brand</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Brand Name"
                      value={form.brand}
                      onChange={(e) => setForm({ ...form, brand: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Category & Price */}
              <Row className="g-3 mt-2">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Category"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Price (£)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Stock & Images */}
              <Row className="g-3 mt-2">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Stock Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={form.countInStock}
                      onChange={(e) => setForm({ ...form, countInStock: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Label>Images (Max 3)</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {preview.map((src, idx) => (
                      <div
                        key={idx}
                        style={{
                          position: 'relative',
                          width: 100,
                          height: 100,
                          flexShrink: 0,
                        }}
                      >
                        <Image
                          src={src}
                          rounded
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            borderRadius: '50%',
                            padding: '0 6px',
                          }}
                          onClick={() => handleRemoveImage(idx)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    {form.images.length < 3 && (
                      <Button
                        as="label"
                        htmlFor="imageUpload"
                        variant="outline-secondary"
                        style={{
                          width: 100,
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          borderRadius: 8,
                        }}
                      >
                        +
                      </Button>
                    )}
                    <input
                      id="imageUpload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAddImage}
                    />
                  </div>
                </Col>
              </Row>

              {/* Description */}
              <Form.Group className="mt-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Product description..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                />
              </Form.Group>

              {/* Submit */}
              <div className="d-grid mt-3">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? 'Uploading...' : 'Submit Product'}
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default AddProduct;
